import React, { Component, ErrorInfo, ReactNode } from "react";
import { Box, Button, Typography, Paper } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import HomeIcon from "@mui/icons-material/Home";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { clearAllCaches, getStorageStats } from "../utils/safeStorage";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  isQuotaError: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isQuotaError: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    const isQuotaError =
      error.name === "QuotaExceededError" ||
      error.message?.includes("quota") ||
      error.message?.includes("storage");

    // Detect ChunkLoadError (happens after new deploy with old cache)
    const isChunkLoadError =
      error.name === "ChunkLoadError" ||
      error.message?.includes("Loading chunk") ||
      error.message?.includes("ChunkLoadError");

    // Auto-reload on ChunkLoadError (only once to avoid infinite loop)
    if (isChunkLoadError) {
      const hasReloaded = sessionStorage.getItem("chunk-error-reloaded");
      if (!hasReloaded) {
        sessionStorage.setItem("chunk-error-reloaded", "true");
        setTimeout(() => {
          window.location.reload();
        }, 100);
      }
    }

    return {
      hasError: true,
      isQuotaError,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    if (this.state.isQuotaError) {
      this.handleClearStorage();
    }
  }

  handleClearStorage = () => {
    try {
      clearAllCaches();
    } catch (e) {
      // Silent cleanup
    }
  };

  handleReload = () => {
    // Clear the reload flag before reloading
    sessionStorage.removeItem("chunk-error-reloaded");
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isQuotaError: false,
    });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "background.default",
            p: 3,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              maxWidth: 600,
              width: "100%",
              p: 4,
              textAlign: "center",
            }}
          >
            <ErrorOutlineIcon
              sx={{
                fontSize: 80,
                color: "error.main",
                mb: 2,
              }}
            />

            <Typography variant="h4" gutterBottom>
              {this.state.isQuotaError
                ? "Armazenamento Cheio"
                : "Algo deu errado"}
            </Typography>

            <Typography variant="body1" color="text.secondary" paragraph>
              {this.state.isQuotaError
                ? "Your browser storage is full. We clear the cache automatically. Please reload the page."
                : "An unexpected error occurred in the application. You can try reloading the page or returning to the homepage."}
            </Typography>

            {process.env.NODE_ENV === "development" && this.state.error && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  bgcolor: "grey.100",
                  borderRadius: 1,
                  textAlign: "left",
                  maxHeight: 200,
                  overflow: "auto",
                }}
              >
                <Typography
                  variant="caption"
                  component="pre"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "0.75rem",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                mt: 4,
                display: "flex",
                gap: 2,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={this.handleReload}
              >
                Recarregar Página
              </Button>

              <Button
                variant="outlined"
                startIcon={<HomeIcon />}
                onClick={this.handleGoHome}
              >
                Ir para Início
              </Button>

              {this.state.isQuotaError && (
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={() => {
                    this.handleClearStorage();
                    this.handleReload();
                  }}
                >
                  Limpar Cache e Recarregar
                </Button>
              )}
            </Box>

            {process.env.NODE_ENV === "development" && (
              <Button
                variant="text"
                size="small"
                onClick={this.handleReset}
                sx={{ mt: 2 }}
              >
                [Dev] Resetar Error Boundary
              </Button>
            )}
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

export function useErrorHandler() {
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  return setError;
}
